import { useState, useEffect, useMemo, useCallback } from 'preact/hooks';
import { bootOnce } from '../../bootstrap';
import { navigate } from '../../router/router';
import { useI18n } from '../../bootstrap/i18n';
import { getAccessTokenOrNull } from '../../utils/api';
import { reportHandledError } from '../../monitoring/uiErrorSink';

interface Gift {
  id: number;
  name: string;
  description: string | null;
  image_url: string | null;
  gift_type: string;
  value: number | null;
  external_url: string | null;
}

interface UserGift {
  id: number;
  status: 'pending' | 'claimed' | 'expired';
  source: string | null;
  source_id: number | null;
  created_at: string;
  claimed_at: string | null;
  expires_at: string | null;
  gift: Gift | null;
}

/**
 * Hook to fetch and manage user gifts count for badge display
 */
export function useGiftsCount() {
  const services = useMemo(() => bootOnce(), []);
  const [count, setCount] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const accessToken = await getAccessTokenOrNull(services, { timeoutMs: 3000 });
      if (!accessToken) return;

      const res = await fetch('/api/user/gifts/count', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const json = await res.json();
      if (json.ok) {
        setCount(json.count || 0);
      }
    } catch (err) {
      // Silent fail for badge count
    }
  }, [services]);

  useEffect(() => {
    refresh();
    // Refresh periodically
    const interval = setInterval(refresh, 60000);
    return () => clearInterval(interval);
  }, [refresh]);

  return { count, refresh };
}

export function GiftsRoute() {
  const services = useMemo(() => bootOnce(), []);
  const { t } = useI18n(services.i18n);
  const [loading, setLoading] = useState(true);
  const [gifts, setGifts] = useState<UserGift[]>([]);
  const [claimingId, setClaimingId] = useState<number | null>(null);
  const [claimedToast, setClaimedToast] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const accessToken = await getAccessTokenOrNull(services, { timeoutMs: 5000 });
        if (!accessToken || cancelled) return;

        const res = await fetch('/api/user/gifts', {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const json = await res.json();
        if (cancelled) return;

        if (json.ok) {
          setGifts(json.gifts || []);
        }
      } catch (err) {
        if (!cancelled) {
          reportHandledError({
            err,
            code: 'gifts.load_failed',
            userMessage: 'Failed to load gifts',
            severity: 'error',
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [services]);

  async function claimGift(userGiftId: number) {
    if (claimingId) return;
    setClaimingId(userGiftId);

    try {
      const accessToken = await getAccessTokenOrNull(services, { timeoutMs: 3000 });
      if (!accessToken) throw new Error('Not authenticated');

      const res = await fetch(`/api/user/gifts/${userGiftId}/claim`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const json = await res.json();

      if (json.ok) {
        // Update local state
        setGifts((prev) =>
          prev.map((g) =>
            g.id === userGiftId
              ? { ...g, status: 'claimed', claimed_at: new Date().toISOString() }
              : g
          )
        );

        // Show toast
        let toastMsg = json.gift_name || t('gifts.claimed', 'Gift claimed!');
        if (json.reward?.type === 'credits') {
          toastMsg = `+${json.reward.value} ${t('gifts.credits', 'credits')}`;
        } else if (json.reward?.type === 'spins') {
          toastMsg = `+${json.reward.value} ${t('gifts.spins', 'free spins')}`;
        }
        setClaimedToast(toastMsg);
        setTimeout(() => setClaimedToast(null), 3000);

        // Open external URL if applicable (via server redirect so we can log/control it)
        if (json.reward?.type === 'external' && json.reward.outbound_url) {
          const tg = (window as any).Telegram?.WebApp;
          if (tg?.openLink) {
            tg.openLink(json.reward.outbound_url);
          } else {
            window.open(json.reward.outbound_url, '_blank', 'noopener,noreferrer');
          }
        }

        // Haptic feedback
        const tg = (window as any).Telegram?.WebApp;
        tg?.HapticFeedback?.notificationOccurred?.('success');
      } else {
        throw new Error(json.error || 'Claim failed');
      }
    } catch (err: any) {
      const tg = (window as any).Telegram?.WebApp;
      tg?.HapticFeedback?.notificationOccurred?.('error');
      alert(err.message || 'Failed to claim gift');
    } finally {
      setClaimingId(null);
    }
  }

  const pendingGifts = gifts.filter((g) => g.status === 'pending');
  const claimedGifts = gifts.filter((g) => g.status === 'claimed');
  const expiredGifts = gifts.filter((g) => g.status === 'expired');

  return (
    <div class="contentDefault" style={{ padding: '16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <div style={{ fontSize: 28 }}>🎁</div>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#fff' }}>
          {t('gifts.title', 'Gifts')}
        </h1>
        {pendingGifts.length > 0 && (
          <div
            style={{
              background: '#DC143C',
              color: '#fff',
              borderRadius: 12,
              padding: '2px 8px',
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {pendingGifts.length}
          </div>
        )}
      </div>

      {/* Toast */}
      {claimedToast && (
        <div
          style={{
            position: 'fixed',
            top: 80,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
            color: '#000',
            padding: '12px 24px',
            borderRadius: 20,
            fontSize: 16,
            fontWeight: 700,
            zIndex: 1000,
            boxShadow: '0 4px 20px rgba(255, 215, 0, 0.4)',
            animation: 'fadeIn 0.3s ease',
          }}
        >
          ✨ {claimedToast}
        </div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: 40, color: 'rgba(255,255,255,0.5)' }}>
          {t('gifts.loading', 'Loading...')}
        </div>
      )}

      {!loading && gifts.length === 0 && (
        <div
          style={{
            textAlign: 'center',
            padding: 60,
            background: 'rgba(26, 10, 10, 0.6)',
            borderRadius: 20,
            border: '1px solid rgba(220, 20, 60, 0.15)',
          }}
        >
          <div style={{ fontSize: 64, marginBottom: 16, opacity: 0.4 }}>📦</div>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#fff', marginBottom: 8 }}>
            {t('gifts.empty_title', 'No gifts yet')}
          </div>
          <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', marginBottom: 20 }}>
            {t('gifts.empty_subtitle', 'Complete tasks to earn rewards')}
          </div>
          <button
            class="primaryBtn"
            onClick={() => navigate({ name: 'tasks' })}
            style={{ padding: '12px 32px' }}
          >
            {t('gifts.go_tasks', 'Go to Tasks')}
          </button>
        </div>
      )}

      {/* Pending gifts */}
      {pendingGifts.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.6)', marginBottom: 12 }}>
            {t('gifts.pending', 'Available')}
          </div>
          {pendingGifts.map((ug) => (
            <GiftCard
              key={ug.id}
              userGift={ug}
              onClaim={() => claimGift(ug.id)}
              claiming={claimingId === ug.id}
              t={t}
            />
          ))}
        </div>
      )}

      {/* Claimed gifts */}
      {claimedGifts.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.4)', marginBottom: 12 }}>
            {t('gifts.claimed_section', 'Claimed')}
          </div>
          {claimedGifts.map((ug) => (
            <GiftCard key={ug.id} userGift={ug} t={t} />
          ))}
        </div>
      )}

      {/* Expired gifts */}
      {expiredGifts.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.3)', marginBottom: 12 }}>
            {t('gifts.expired_section', 'Expired')}
          </div>
          {expiredGifts.map((ug) => (
            <GiftCard key={ug.id} userGift={ug} t={t} />
          ))}
        </div>
      )}
    </div>
  );
}

interface GiftCardProps {
  userGift: UserGift;
  onClaim?: () => void;
  claiming?: boolean;
  t: (key: string, fallback: string) => string;
}

function GiftCard({ userGift, onClaim, claiming, t }: GiftCardProps) {
  const gift = userGift.gift;
  const isPending = userGift.status === 'pending';
  const isClaimed = userGift.status === 'claimed';
  const isExpired = userGift.status === 'expired';

  // Check if expiring soon (within 24 hours)
  const expiresSoon =
    isPending &&
    userGift.expires_at &&
    new Date(userGift.expires_at).getTime() - Date.now() < 24 * 60 * 60 * 1000;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: 16,
        marginBottom: 12,
        background: isExpired
          ? 'rgba(30, 30, 30, 0.5)'
          : isPending
            ? 'rgba(26, 10, 10, 0.8)'
            : 'rgba(26, 10, 10, 0.5)',
        borderRadius: 16,
        border: isPending
          ? '1px solid rgba(255, 215, 0, 0.3)'
          : '1px solid rgba(220, 20, 60, 0.15)',
        boxShadow: isPending ? '0 0 20px rgba(255, 215, 0, 0.1)' : 'none',
        opacity: isExpired ? 0.5 : 1,
      }}
    >
      {/* Gift image */}
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: 12,
          background: 'rgba(255,255,255,0.05)',
          flexShrink: 0,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {gift?.image_url ? (
          <img
            src={gift.image_url}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <span style={{ fontSize: 32 }}>🎁</span>
        )}
      </div>

      {/* Gift info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: isExpired ? 'rgba(255,255,255,0.4)' : '#fff',
            marginBottom: 4,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {gift?.name || t('gifts.unknown', 'Gift')}
        </div>
        {gift?.description && (
          <div
            style={{
              fontSize: 14,
              color: 'rgba(255,255,255,0.5)',
              marginBottom: 4,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {gift.description}
          </div>
        )}
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>
          {userGift.source && (
            <span>
              {t('gifts.from', 'From')}: {userGift.source}
            </span>
          )}
          {expiresSoon && (
            <span style={{ color: '#FFA500', marginLeft: 8 }}>
              ⏰ {t('gifts.expires_soon', 'Expires soon')}
            </span>
          )}
        </div>
      </div>

      {/* Claim button / status */}
      <div style={{ flexShrink: 0 }}>
        {isPending && onClaim && (
          <button
            onClick={onClaim}
            disabled={claiming}
            style={{
              padding: '10px 20px',
              background: claiming
                ? 'rgba(255, 215, 0, 0.3)'
                : 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
              border: 'none',
              borderRadius: 12,
              color: '#000',
              fontSize: 14,
              fontWeight: 700,
              cursor: claiming ? 'wait' : 'pointer',
              animation: claiming ? 'none' : 'pulse 2s infinite',
            }}
          >
            {claiming ? '...' : t('gifts.claim', 'Claim')}
          </button>
        )}
        {isClaimed && (
          <div
            style={{
              padding: '8px 16px',
              background: 'rgba(76, 175, 80, 0.2)',
              borderRadius: 12,
              color: '#4CAF50',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            ✓ {t('gifts.claimed', 'Claimed')}
          </div>
        )}
        {isExpired && (
          <div
            style={{
              padding: '8px 16px',
              background: 'rgba(150, 150, 150, 0.2)',
              borderRadius: 12,
              color: 'rgba(255,255,255,0.4)',
              fontSize: 13,
            }}
          >
            {t('gifts.expired', 'Expired')}
          </div>
        )}
      </div>
    </div>
  );
}
